import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const viralHookFaqs = [
  {
    question: "What is a viral hook generator?",
    answer:
      "A viral hook generator is a tool that helps creators come up with stronger opening lines for content. The goal is to improve attention, curiosity, and clarity right at the start."
  },
  {
    question: "Who is Hookora’s Viral Hook Generator for?",
    answer:
      "It is built for creators, content marketers, personal brands, and anyone who wants stronger openings for videos, posts, captions, or scripts."
  },
  {
    question: "Can I use it for YouTube Shorts, Instagram Reels, and TikTok?",
    answer:
      "Yes. Hookora is designed to support short-form creator workflows, including hook ideation that can be adapted for different platforms."
  },
  {
    question: "Does Hookora only generate hooks?",
    answer:
      "No. Hookora also includes tools for titles, captions, hashtags, descriptions, short scripts, and thumbnail text, so your workflow can stay connected."
  },
  {
    question: "Why do hooks matter so much?",
    answer:
      "The first line often decides whether someone keeps watching, reading, or scrolling. A stronger hook gives your content a better chance to hold attention."
  },
  {
    question: "Can beginners use this tool?",
    answer:
      "Yes. Hookora is built to feel simple and useful whether you are just starting or already creating content regularly."
  }
];

function ViralHookGeneratorPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Viral Hook Generator | Hookora"
        description="Use Hookora’s Viral Hook Generator to create stronger opening lines for videos and social content with better clarity, curiosity, and stopping power."
        path="/viral-hook-generator"
      />
      <StructuredData
        id="viral-hook-generator-faq-schema"
        data={buildFAQSchema(viralHookFaqs)}
      />

      <Navbar />

      <ToolSeoHero
        badge="Hookora Tool • Viral Hook Generator"
        title="Viral Hook Generator for creators who want stronger attention from the first line"
        description="The Hookora Viral Hook Generator helps creators come up with stronger opening lines for videos, captions, and short-form content. Instead of guessing how to start, you get sharper hooks built to increase curiosity, clarity, and stopping power."
        highlights={[
          "Generate faster",
          "Improve first impressions",
          "Write with more confidence",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Viral Hook Generator helps you do"
        previewItems={[
          {
            label: "Attention",
            text: "Create stronger opening lines that help your content get noticed faster."
          },
          {
            label: "Clarity",
            text: "Turn vague ideas into hook directions that feel sharper and easier to use."
          },
          {
            label: "Momentum",
            text: "Keep creating even when you feel stuck on how to begin."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A viral hook generator should make content creation feel lighter, not more confusing"
        description="The best hooks are not random. They are clear, emotionally interesting, and strong enough to make someone keep watching. Hookora helps you reach that faster."
        benefits={[
          {
            title: "Start faster with less friction",
            text: "Instead of staring at a blank screen, get immediate hook directions that help you move from idea to output faster."
          },
          {
            title: "Make your first line work harder",
            text: "A stronger opening can change how people respond to the rest of your content. Hookora helps you improve that first impression."
          },
          {
            title: "Create more options from one topic",
            text: "A single content idea can lead to multiple hook angles. This helps you test stronger wording without overthinking."
          },
          {
            title: "Stay consistent across your workflow",
            text: "Your hooks can connect naturally to titles, captions, scripts, and descriptions inside the wider Hookora system."
          },
          {
            title: "Sound sharper without forcing it",
            text: "The goal is not robotic content. The goal is to help your message feel stronger, clearer, and more intentional."
          },
          {
            title: "Useful for short-form and social content",
            text: "Whether you create for YouTube, Instagram, TikTok, or other platforms, stronger hooks improve how quickly your idea lands."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Viral Hook Generator FAQ"
        intro="Here are a few common questions about how Hookora’s Viral Hook Generator works."
        faqs={viralHookFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Viral Hook Generator to create stronger openings faster"
        description="If you want better first lines, clearer hook options, and a smoother content workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default ViralHookGeneratorPage;